import { CloseOutlined, EditOutlined, ExclamationCircleOutlined } from "@ant-design/icons"
import { supportedDocumentFormats } from "@finway-group/shared/lib/consts/common"
import { CollectionNameEnum, FileExtEnum, RightEnum, UploadKindEnum, Vendor } from "@finway-group/shared/lib/models"
import { removeQueryString } from "@finway-group/shared/lib/utils"
import { Alert, Button, Card, Modal, Row, Space, Tabs, Tooltip, Upload } from "antd"
import React, { useState } from "react"
import { AlertTriangle, X as CancelIcon, Check as CheckIcon } from "react-feather"
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"
import { useHistory, useParams } from "react-router-dom"

import { PageCustomHeader } from "Components/PageCustomHeader"
import VendorImage from "Components/VendorImage"
import { CommentContainer } from "Components/comment/comment.container"
import { FileViewerCached } from "Components/fileViewer/FileViewerCached"
import GroupedExpenses from "Components/groupedExpenses"
import { Loading } from "Components/loading"
import { VendorFormModal } from "Components/modals/vendor.modal"
import { VendorRuleModal } from "Components/modals/vendorRule.modal"
import { API_URL } from "Shared/config/consts"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import { useApproveVendorStatus, useHardDeleteVendor, useSoftDeleteVendor, useUpdateVendorContracts } from "Shared/queries/vendors/vendor.mutations"
import { useVendor } from "Shared/queries/vendors/vendor.queries"
import { RoutePathEnum } from "Shared/router/RoutePath.enum"
import { AuthzService, FileService, NotificationService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
import { ErrorInterface } from "Shared/utils/errorHandler.utils"
import { isFileExtensionMatching } from "Shared/utils/file.utils"
import { getTooltipPopupContainer } from "Shared/utils/helper.utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"

import { VendorBusinessDetails } from "./vendorBusinessDetails"
import { VendorContactDetails } from "./vendorContactDetails"
import { VendorDetailRules } from "./vendorDetailsRules"

const { Dragger } = Upload
const { confirm } = Modal
const { TabPane } = Tabs

interface IMatchParams {
    id: string
}

const VendorDetail: React.FC<{}> = () => {
    const { t } = useTranslation()
    const history = useHistory()
    const { id } = useParams<IMatchParams>()
    const { data: vendor, isError, isLoading } = useVendor(id)
    const softDeleteVendorMutation = useSoftDeleteVendor()
    const hardDeleteVendorMutation = useHardDeleteVendor()
    const approveVendorStatusMutation = useApproveVendorStatus()
    const updateVendorContractsMutation = useUpdateVendorContracts()

    const loggedInUser = useLoggedInEmployeeProfile()

    const [isUploading, setIsUploading] = useStateIfMounted(false)
    const [isModalShowing, setIsModalShowing] = useState(false)
    const [isRuleModalShowing, setIsRuleModalShowing] = useState(false)
    const [isUpdatingVendorStatus, setIsUpdatingVendorStatus] = useStateIfMounted(false)

    const canEditContracts = !vendor?.deleted && AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR_CONTRACT__ALL__UPDATE)
    const canViewContracts = AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR_CONTRACT__ALL__READ)

    if (isError) {
        history.push(RoutePathEnum.VENDORS)
    }
    if (isLoading) {
        return <Loading />
    }
    if (!vendor) {
        return null
    }

    const approveOrRejectVendor = async (isVendorApproved: boolean) => {
        setIsUpdatingVendorStatus(true)
        try {
            if (isVendorApproved) {
                await approveVendorStatusMutation.mutateAsync(vendor.id)
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.approved.title"), t("notification:vendor.approved.message"))
            } else {
                await hardDeleteVendorMutation.mutateAsync(vendor.id)
                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.rejected.title"), t("notification:vendor.rejected.message"))
                history.push({ pathname: RoutePathEnum.VENDORS })
            }
        } catch {
            NotificationService.showErrorNotificationBasedOnResponseError(t("error:update.title"), t("error:update.message"))
        } finally {
            setIsUpdatingVendorStatus(false)
        }
    }

    const handleDelete = () => {
        confirm({
            title: t("confirm:vendor.delete.title"),
            icon: <ExclamationCircleOutlined />,
            content: t("confirm:vendor.delete.message"),
            okText: t("confirm:vendor.delete.confirm"),
            cancelText: t("confirm:vendor.delete.cancel"),
            onOk() {
                return handleDeletionConfirmation(vendor.id, vendor)
            },
            onCancel() {},
        })
    }

    const handleDeletionConfirmation = (vendorId: string, updateVendorData: Vendor) =>
        softDeleteVendorMutation.mutate(
            { vendorId, updateVendorData },
            {
                onSuccess: () => {
                    NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:vendor.deleted.title"), t("notification:vendor.deleted.message"))
                    history.push({ pathname: RoutePathEnum.VENDORS })
                },
                onError: (error: ErrorInterface | any) => {
                    NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:vendor.delete.title"))
                },
            },
        )

    const doFileUpload = (file: any) => {
        if (!FileService.checkFileSizeAndHandleError(file) || !vendor) {
            return
        }
        if (!FileService.checkIsFileExtensionValidAndShowNotification(file, supportedDocumentFormats as ReadonlyArray<string>)) {
            return
        }

        setIsUploading(true)

        FileService.upload(file, UploadKindEnum.VENDOR_CONTRACT)
            .then((data) => {
                setIsUploading(false)
                vendor.contracts.push(data.url)

                updateVendorContractsMutation.mutate(
                    { vendorId: vendor.id, vendorContracts: vendor.contracts },
                    {
                        onSuccess: () => {
                            NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:file.uploaded.title"), t("notification:file.uploaded.message"))
                        },
                        onError: (error: ErrorInterface | any) => {
                            NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:error"))
                        },
                    },
                )
            })
            .catch((error: Error) => {
                setIsUploading(false)
                NotificationService.showErrorNotificationBasedOnResponseError(error, t("error:file.upload.title"))
            })
    }

    const handleRemoveTab = (targetKey: string) => {
        if (!vendor) {
            return
        }
        confirm({
            title: t("confirm:file.delete_from_server.title"),
            icon: <ExclamationCircleOutlined />,
            content: t("confirm:file.delete_from_server.message"),
            cancelText: t("confirm:file.delete_from_server.cancel"),
            okText: t("confirm:file.delete_from_server.confirm"),
            onOk() {
                return new Promise<boolean>((resolve, reject) => {
                    vendor.contracts.splice(Number(targetKey), 1)

                    updateVendorContractsMutation.mutate(
                        { vendorId: vendor.id, vendorContracts: vendor.contracts },
                        {
                            onSuccess: () => {
                                resolve(true)
                                NotificationService.send(NotificationTypeEnum.SUCCESS, t("notification:file.deleted.title"), t("notification:file.deleted.message"))
                            },
                            onError: (error: ErrorInterface | any) => {
                                reject(error)
                                NotificationService.send(NotificationTypeEnum.ERROR, t("error:file.delete.title"), t("error:file.delete.message"))
                            },
                        },
                    )
                }).catch(() => {}) // no-op ignore error
            },
            onCancel() {},
        })
    }

    const handleEditTab = (targetKey: string, action: "add" | "remove") => action === "remove" && handleRemoveTab(targetKey)

    const openRuleModal = () => setIsRuleModalShowing(true)

    return (
        <>
            <Helmet>
                <title>{vendor.name}</title>
            </Helmet>
            {vendor.deleted && (
                <Alert
                    className="mb-20"
                    message={t("notification:vendor.already_deleted.title")}
                    type="error"
                    description={t("notification:vendor.already_deleted.message")}
                    closable={false}
                    showIcon={true}
                />
            )}

            <PageCustomHeader
                title={
                    <div className="flex max-w-200 xs:max-w-300 sm:max-w-450 md:max-w-3xl">
                        <div>
                            <VendorImage size={55} vendor={vendor} />
                        </div>
                        <div className="flex flex-col ml-8 mt-2 overflow-x-auto no-scrollbar">
                            <div className="flex items-center">
                                <h1 className="mb-3 overflow-x-auto no-scrollbar">{vendor.name}</h1>
                                {!vendor.isApproved && (
                                    <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("info:pending_vendor")}>
                                        <AlertTriangle fill="#FBAC3B" size={26} stroke="#FFFFFF" className="ml-4" />
                                    </Tooltip>
                                )}
                            </div>
                            <p className="leading-tight overflow-x-auto no-scrollbar">{vendor.description}</p>
                        </div>
                    </div>
                }
                actionButtons={
                    vendor.deleted ? (
                        <></>
                    ) : vendor.isApproved ? (
                        <>
                            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && (
                                <Button className="btn-default" onClick={handleDelete} icon={<CloseOutlined />} data-testid="finway:vendorDetails:delete-btn">
                                    <span>{t("action:delete")}</span>
                                </Button>
                            )}
                            {(AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) || loggedInUser.id === vendor.createdBy) && (
                                <Button className="ml-10 btn-default" onClick={() => setIsModalShowing(true)} icon={<EditOutlined />}>
                                    <span>{t("action:edit")}</span>
                                </Button>
                            )}
                        </>
                    ) : (
                        <>
                            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.VENDOR__ALL__UPDATE) && (
                                <>
                                    <Tooltip title={t("info:reject_vendor")}>
                                        <Button className="btn-default" onClick={() => approveOrRejectVendor(false)} icon={<CancelIcon />}>
                                            <span>{t("action:reject")}</span>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title={t("info:approve_vendor")}>
                                        <Button
                                            loading={isUpdatingVendorStatus}
                                            disabled={isUpdatingVendorStatus}
                                            type="primary"
                                            onClick={() => approveOrRejectVendor(true)}
                                            icon={<CheckIcon />}
                                        >
                                            <span>{t("action:approve_vendor")}</span>
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                        </>
                    )
                }
            />

            {isModalShowing && <VendorFormModal isNew={false} isShowing={isModalShowing} onCancel={setIsModalShowing} />}
            {isRuleModalShowing && <VendorRuleModal isShowing={isRuleModalShowing} onCancel={setIsRuleModalShowing} vendor={vendor} />}

            <div className="grid md:grid-cols-3 md:auto-rows-auto gap-24 sm:grid-cols-1">
                <Card className="w-full" id="vendor-source-section">
                    <Tabs defaultActiveKey="1" className="mb-8">
                        <TabPane key="rule" tab={t("label:rules")}>
                            <VendorDetailRules vendor={vendor} openRuleModal={openRuleModal} />
                        </TabPane>
                        <TabPane key="vendorData" tab={t("label:business_details")}>
                            <VendorBusinessDetails vendor={vendor} isUploading={isUploading} canEditContracts={canEditContracts} doFileUpload={doFileUpload} />
                        </TabPane>
                        <TabPane key="transactionData" tab={t("label:contact_details")}>
                            <VendorContactDetails vendor={vendor} />
                        </TabPane>
                    </Tabs>
                </Card>
                <div className="md:col-span-2 md:row-start-1 md:row-end-4">
                    <Row className="-mt-10 sm:mt-0">
                        <Card className="w-full card-with-tabs">
                            <Tabs className="w-full min-h-200 ml-1">
                                <Tabs.TabPane key="contracts" tab={t("label:contracts")}>
                                    {vendor.contracts?.length === 0 ? (
                                        <Space style={{ display: "flex" }} className="pr-10 pl-10">
                                            <Dragger
                                                action={`${API_URL}/files`}
                                                headers={{
                                                    Authorization: `Bearer ${localStorage.accessToken}`,
                                                }}
                                                accept={supportedDocumentFormats.join(",")}
                                                multiple={false}
                                                showUploadList={false}
                                                customRequest={(req) => {
                                                    doFileUpload(req.file)
                                                }}
                                                disabled={vendor.deleted || !vendor.isApproved || !canEditContracts}
                                            >
                                                {!vendor.deleted && (
                                                    <div className="el-upload__text">
                                                        <i className="el-icon-upload" />
                                                        <br />
                                                        <Button loading={isUploading} disabled={!vendor.isApproved || !canEditContracts}>
                                                            {t("action:add_contract")}
                                                        </Button>
                                                        <br />
                                                        <small>{t("action:or_drop_files")}</small>
                                                    </div>
                                                )}
                                            </Dragger>
                                        </Space>
                                    ) : (
                                        <div>
                                            <Tabs type={canEditContracts ? "editable-card" : "card"} hideAdd defaultActiveKey={`${0}`} onEdit={handleEditTab}>
                                                {vendor.contracts?.map((filePath: string, index: number) => (
                                                    <Tabs.TabPane key={`${index}`} closable tab={`${t("label:contract_document")} ${index + 1}`} forceRender>
                                                        {isFileExtensionMatching(filePath, FileExtEnum.PDF) ? (
                                                            <FileViewerCached key={removeQueryString(filePath)} filePath={filePath} />
                                                        ) : (
                                                            <img alt="Failed to load file" src={filePath} />
                                                        )}
                                                    </Tabs.TabPane>
                                                ))}
                                            </Tabs>
                                        </div>
                                    )}
                                </Tabs.TabPane>
                            </Tabs>
                        </Card>
                    </Row>
                    {AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__TEAM__READ) && (
                        <Row>
                            <div className={canViewContracts ? "mt-24" : "mt-0"} style={{ width: "100%" }}>
                                <GroupedExpenses groupedByField="vendor._id" groupedById={vendor.id} />
                            </div>
                        </Row>
                    )}
                </div>
                <CommentContainer type={CollectionNameEnum.VENDOR} comments={vendor.comments} object={vendor} />
            </div>
        </>
    )
}

export default VendorDetail
